/**
 * featureUsageQueue.ts
 *
 * Pure bookkeeping for beta-feature usage counts waiting to be sent. A queue
 * maps "feature|YYYY-MM-DD|event" to a count; the store persists it in
 * localStorage and sends it in batches.
 */

/** Counts waiting to be sent, keyed by "feature|date|event". */
export type UsageQueue = Record<string, number>

/** One queued count, in the shape sent to the server. */
export interface UsageEntry {
  feature: string
  date: string
  event: string
  count: number
}

const SEP = '|'

/** Returns a copy of queue with one more event on date (YYYY-MM-DD). */
export function addUsage(queue: UsageQueue, feature: string, event: string, date: string): UsageQueue {
  const key = [feature, date, event].join(SEP)
  return { ...queue, [key]: (queue[key] ?? 0) + 1 }
}

/** Lists up to limit well-formed entries in key order. */
export function queueEntries(queue: UsageQueue, limit: number): UsageEntry[] {
  return Object.keys(queue)
    .sort()
    .map((key) => toEntry(key, queue[key]))
    .filter((e): e is UsageEntry => e !== null)
    .slice(0, limit)
}

/** Returns queue minus the counts in sent; keys that reach zero are dropped. */
export function subtractEntries(queue: UsageQueue, sent: UsageEntry[]): UsageQueue {
  const next = { ...queue }
  for (const e of sent) {
    const key = [e.feature, e.date, e.event].join(SEP)
    const left = (next[key] ?? 0) - e.count
    if (left > 0) next[key] = left
    else delete next[key]
  }
  return next
}

// =============================================================================
// Helper Functions
// =============================================================================

function toEntry(key: string, count: unknown): UsageEntry | null {
  const parts = key.split(SEP)
  if (parts.length !== 3 || typeof count !== 'number' || !Number.isInteger(count) || count < 1) return null
  const [feature, date, event] = parts
  return { feature, date, event, count }
}
